import { FC } from 'react';

import { resolveImageSrc } from '../../api/client';
import { formatMoney } from '../../shared/functions/formatter';
import { Card, CardTitle, Meta } from '../../ui';
import './TillPreview.css';

/**
 * What the preview rail can show, in the shape the till would draw it.
 *
 * The colours are the category's, because palette2 and palette3 really are the
 * background and text of its item cards on the till (see ItemSelector). Seeing
 * them applied is the quickest way to judge a palette change.
 */
export type Preview =
  | {
      kind: 'category';
      name: string;
      palette2: string;
      palette3: string;
      items: { id: number; title: string; priceCents: number; img: string }[];
    }
  | {
      kind: 'bundle';
      name: string;
      priceCents: number;
      /** Cheapest legal pick across every group, at current prices. */
      regularMinCents: number;
      /** Dearest legal pick across every group. */
      regularMaxCents: number;
      /** One "N from X" line per requirement group. */
      groups: { quantity: number; label: string }[];
    };

const TillPreview: FC<{ preview: Preview | null }> = ({ preview }) => {
  if (!preview) {
    return (
      <Card className="tillPreview">
        <CardTitle className="tillPreviewTitle">Preview</CardTitle>
        <Meta className="tillPreviewHint">Add a category to see how its cards look on the till.</Meta>
      </Card>
    );
  }

  if (preview.kind === 'category') {
    // The till falls back to plain black on white when a colour is unset.
    const background = preview.palette2 || '#ffffff';
    const foreground = preview.palette3 || '#1b1b1b';
    return (
      <Card className="tillPreview">
        <CardTitle className="tillPreviewTitle">On the till</CardTitle>
        <Meta className="tillPreviewHint">{preview.name}</Meta>
        <div className="tillPreviewGrid">
          {preview.items.map((item) => (
            <div className="tillPreviewCard" key={item.id} style={{ backgroundColor: background, color: foreground }}>
              {item.img ? <img src={resolveImageSrc(item.img)} alt="" /> : <span className="tillPreviewNoImg" />}
              <span className="tillPreviewName">{item.title}</span>
              <span className="tillPreviewPrice">{formatMoney(item.priceCents)}</span>
            </div>
          ))}
          {preview.items.length === 0 && <Meta className="tillPreviewHint">No items yet.</Meta>}
        </div>
        <Meta className="tillPreviewNote">Colours are this category’s card background and text.</Meta>
      </Card>
    );
  }

  const savingMin = preview.regularMinCents - preview.priceCents;
  const savingMax = preview.regularMaxCents - preview.priceCents;
  const range = (low: number, high: number) => (low === high ? formatMoney(low) : formatMoney(low) + ' – ' + formatMoney(high));
  return (
    <Card className="tillPreview">
      <CardTitle className="tillPreviewTitle">On the till</CardTitle>
      <div className="tillPreviewBundle">
        <span className="tillPreviewName">{preview.name}</span>
        <span className="tillPreviewParts">{preview.groups.map((group) => group.quantity + '× from ' + group.label).join(' · ')}</span>
        <span className="tillPreviewPrice">{formatMoney(preview.priceCents)}</span>
        {savingMax > 0 && <span className="tillPreviewSaving">save up to {formatMoney(savingMax)}</span>}
      </div>
      <dl className="tillPreviewPricing">
        <div>
          <dt>Eligible regular price</dt>
          <dd>{range(preview.regularMinCents, preview.regularMaxCents)}</dd>
        </div>
        <div>
          <dt>Bundle price</dt>
          <dd>{formatMoney(preview.priceCents)}</dd>
        </div>
        <div className="tillPreviewPricingSave">
          <dt>Customer saves</dt>
          <dd>{range(savingMin, savingMax)}</dd>
        </div>
      </dl>
    </Card>
  );
};

export default TillPreview;
